/**
 * All classes and functions related to chapter page groups
 * Chapter page groups are class wrappers for a single chapter that manage the order and quality of pages
 */

import {
  Chapter,
  type ChapterEntity,
  type ChapterImageQualities,
} from "~/src/api";
import { ImagePageLoader } from "~/src/reader/managers/cdnImage";
import { createChapterPages, groupPages } from "~/src/reader/utils";
import type { QueryClient } from "@tanstack/vue-query";
import { useChapterPageGet } from "~/utils/tanstack/chapter";

export class ChapterPageManager {
  pagePromise?: ReturnType<typeof Chapter.getPageUrls>;
  pages?: Awaited<ReturnType<typeof Chapter.getPageUrls>>;
  pageLoaders: ImagePageLoader[] = [];

  constructor(
    public queryClient: () => QueryClient,
    public chapterId: string,
    public quality: ChapterImageQualities = "medium",
    public authTokenFetcher?: AuthFunction,
    public gated?: boolean,
  ) {}

  pagesHaveNotExpired() {
    if (this.pages === undefined) {
      return false;
    }

    return (
      this.pageLoaders.every((page) => page.blob) ||
      (!!this.pages.expiresAt && this.pages.expiresAt < Date.now())
    );
  }

  private async fetchPages() {
    if (this.pagesHaveNotExpired()) return this.pages!;

    this.unloadPages();

    if (this.pagePromise) return await this.pagePromise;
    else {
      this.pagePromise = new Promise(async (resolve, reject) => {
        let fetched;
        try {
          fetched = await this.queryClient().fetchQuery(
            useChapterPageGet(
              this.chapterId,
              this.gated ? this.authTokenFetcher : undefined,
            ),
          );
          resolve(fetched);
        } catch (e) {
          if (this.gated) {
            reject(e);
            return;
          }
          try {
            fetched = await this.queryClient().fetchQuery(
              useChapterPageGet(this.chapterId, this.authTokenFetcher),
            );
            resolve(fetched);
          } catch (e) {
            reject(e);
          }
        }
      });
    }

    let fetched;
    try {
      fetched = await this.pagePromise;
    } catch (e) {
      this.gated = true;
    }

    this.pages = fetched ?? {
      source: [],
      high: [],
      medium: [],
      low: [],
      expiresAt: Date.now() + 5 * 60 * 1000,
    };

    return this.pages;
  }

  private async createLoaders() {
    // dont recreate page loaders if we already have them
    if (this.pageLoaders.length) return this.pageLoaders;

    const pages = await this.fetchPages();

    if (this.pageLoaders.at(0)?.quality !== this.quality) {
      this.pageLoaders.splice(
        0,
        Infinity,
        ...createChapterPages(pages[this.quality], this.quality),
      );
    }

    return this.pageLoaders;
  }

  async getPages(pagesPerGroup = 1) {
    const loaders = await this.createLoaders();
    return groupPages(this.chapterId, loaders, pagesPerGroup);
  }

  async loadPages(reverse = false) {
    if (!this.pageLoaders.filter((page) => !page.loading && !page.blob).length)
      return;

    const loadNext = async () => {
      const needsLoading = this.pageLoaders.filter(
        (page) => !page.loading && !page.blob && !page.error,
      );

      const loadIndex = reverse ? Math.max(needsLoading.length - 1, 0) : 0;
      await needsLoading[loadIndex]?.load().then(() => loadNext());
    };

    await loadNext();
  }

  unloadPages() {
    this.pageLoaders.forEach((page) => page.unload());
    this.pageLoaders = [];
  }

  destroy() {
    this.pageLoaders.forEach((page) => page.destroy());
    this.unloadPages();
  }
}
